import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, Calendar, DollarSign, Briefcase, ArrowLeft,
  Users, Check, Clapperboard, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProductionBoard() {
  const { productionId } = useParams();
  const { user } = useAuth();
  
  const [productions, setProductions] = useState([]);
  const [selectedProduction, setSelectedProduction] = useState(null);
  const [applying, setApplying] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProductions();
  }, []);

  useEffect(() => {
    if (productionId && productions.length) {
      const production = productions.find(p => p.id === productionId);
      setSelectedProduction(production);
    }
  }, [productionId, productions]);

  const fetchProductions = async () => {
    try {
      const res = await fetch(`${API}/api/studio/productions`);
      if (res.ok) {
        const data = await res.json();
        setProductions(data.productions || []);
      }
    } catch (err) {
      console.error('Error fetching productions:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyToProduction = async (productionId, role) => {
    if (!user) {
      toast.error('Please log in to apply');
      return;
    }

    setApplying(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/studio/productions/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          production_id: productionId,
          role: role
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        if (data.badges_earned?.length) {
          toast.success(`Badge earned: ${data.badges_earned.join(', ')}`);
        }
        setSelectedRole(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to apply');
      }
    } catch (err) {
      toast.error('Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const compensationLabels = {
    paid: { label: 'Paid', color: 'bg-emerald-500/20 text-emerald-300' },
    stipend: { label: 'Stipend', color: 'bg-blue-500/20 text-blue-300' },
    stipend_plus_credit: { label: 'Stipend + Credit', color: 'bg-amber-500/20 text-amber-300' }
  };

  // Production Detail View
  if (selectedProduction) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-900 to-violet-950" data-testid="production-detail">
        {/* Header */}
        <div className="border-b border-violet-500/30 py-12">
          <div className="container mx-auto px-4">
            <Link to="/legacystudio/productions" className="inline-flex items-center text-violet-300 hover:text-white mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Productions
            </Link>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Badge variant="outline" className="border-violet-400 text-violet-200">
                {selectedProduction.type}
              </Badge>
              <Badge className={compensationLabels[selectedProduction.compensation]?.color}>
                {compensationLabels[selectedProduction.compensation]?.label}
              </Badge>
              {selectedProduction.cultural_focus && (
                <Badge className="bg-amber-500/20 text-amber-300 border-amber-400">
                  Cultural Focus
                </Badge>
              )}
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">{selectedProduction.title}</h1>
            <p className="text-violet-200 mb-4">by {selectedProduction.production_company}</p>
            <div className="flex flex-wrap gap-4 text-sm text-violet-300">
              <span className="flex items-center"><MapPin className="h-4 w-4 mr-1" /> {selectedProduction.location}</span>
              <span className="flex items-center"><Calendar className="h-4 w-4 mr-1" /> Starts {formatDate(selectedProduction.start_date)}</span>
              <span className="flex items-center capitalize"><Clock className="h-4 w-4 mr-1" /> {selectedProduction.status.replace('_', ' ')}</span>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="bg-white/5 backdrop-blur border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">About This Production</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-violet-100">{selectedProduction.description}</p>
                </CardContent>
              </Card>

              {/* Open Roles */}
              <Card className="bg-white/5 backdrop-blur border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-violet-400" />
                    Open Positions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {selectedProduction.roles_needed?.map((role, idx) => {
                    const isOpen = role.filled < role.slots;
                    
                    return (
                      <div 
                        key={`item-${idx}`}
                        className={`p-4 rounded-lg border ${
                          isOpen ? 'border-violet-500/50 bg-violet-500/10' : 'border-stone-600 bg-stone-800/50 opacity-60'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium text-white">{role.role}</h3>
                            <div className="flex items-center gap-3 text-sm text-violet-300 mt-1">
                              <span className="capitalize">{role.experience} level</span>
                              <span>{role.slots - role.filled} of {role.slots} spots open</span>
                            </div>
                          </div>
                          
                          {isOpen ? (
                            <Button 
                              onClick={() => applyToProduction(selectedProduction.id, role.role)}
                              disabled={applying || !user}
                              className="bg-violet-600 hover:bg-violet-700"
                            >
                              {applying ? "Applying..." : "Apply"}
                            </Button>
                          ) : (
                            <Badge className="bg-stone-600 text-stone-300">Filled</Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="bg-white/5 backdrop-blur border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Quick Info</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-xs text-violet-400 uppercase tracking-wider mb-1">Status</p>
                    <p className="text-white capitalize">{selectedProduction.status.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-violet-400 uppercase tracking-wider mb-1">Compensation</p>
                    <Badge className={compensationLabels[selectedProduction.compensation]?.color}>
                      {compensationLabels[selectedProduction.compensation]?.label}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-violet-400 uppercase tracking-wider mb-1">Start Date</p>
                    <p className="text-white">{formatDate(selectedProduction.start_date)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-violet-400 uppercase tracking-wider mb-1">Location</p>
                    <p className="text-white">{selectedProduction.location}</p>
                  </div>
                </CardContent>
              </Card>

              {!user && (
                <Card className="bg-violet-500/20 border-violet-400/50">
                  <CardContent className="py-6 text-center">
                    <Users className="h-10 w-10 text-violet-300 mx-auto mb-3" />
                    <p className="text-violet-100 mb-4">Log in to apply for positions</p>
                    <Link to="/login">
                      <Button className="bg-violet-600 hover:bg-violet-700">Log In</Button>
                    </Link>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Production List View
  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-900 to-violet-950" data-testid="production-board">
      {/* Header */}
      <div className="border-b border-violet-500/30 py-12">
        <div className="container mx-auto px-4">
          <Link to="/legacystudio" className="inline-flex items-center text-violet-300 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to LegacyStudio
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Clapperboard className="h-8 w-8 text-violet-400" />
            <h1 className="text-3xl font-bold text-white">Production Board</h1>
          </div>
          <p className="text-violet-200">Real opportunities on real sets. Find your next gig.</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {productions.map((production, idx) => {
            const openRoles = production.roles_needed?.reduce(
              (acc, r) => acc + (r.slots - r.filled), 0
            ) || 0;
            
            return (
              <motion.div
                key={production.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full bg-white/5 backdrop-blur border-white/10 hover:border-violet-400/50 transition-all">
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="border-violet-400 text-violet-200">
                        {production.type}
                      </Badge>
                      <Badge className={compensationLabels[production.compensation]?.color}>
                        {compensationLabels[production.compensation]?.label}
                      </Badge>
                    </div>
                    <CardTitle className="text-white">{production.title}</CardTitle>
                    <CardDescription className="text-violet-200">
                      {production.production_company}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-violet-100 text-sm mb-4 line-clamp-2">{production.description}</p>
                    
                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex items-center text-violet-300">
                        <MapPin className="h-4 w-4 mr-2" />
                        {production.location}
                      </div>
                      <div className="flex items-center text-violet-300">
                        <Calendar className="h-4 w-4 mr-2" />
                        Starts {formatDate(production.start_date)}
                      </div>
                      <div className="flex items-center text-emerald-400">
                        <Briefcase className="h-4 w-4 mr-2" />
                        {openRoles} positions open
                      </div>
                    </div>
                    
                    <Link to={`/legacystudio/productions/${production.id}`}>
                      <Button className="w-full bg-violet-600 hover:bg-violet-700">
                        View Details
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {productions.length === 0 && !loading && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-12 text-center">
              <Clapperboard className="h-12 w-12 text-violet-400 mx-auto mb-4" />
              <p className="text-violet-200">No active productions at this time. Check back soon!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
